import type MockAdapter from 'axios-mock-adapter'
import { auditFieldsForCreate, newId } from '../engine/context'
import { insertRow, readTable } from '../store/localDb.store'
import { fail, handle, parse } from './c07.shared'
import {
  type ImportCheck,
  type ImportRow,
  type ImportRowsBody,
  importFileOf,
  importRowsOf,
  importSummary,
  masterLog,
  requireWrite,
  text,
  up
} from './masterData.shared'

/**
 * MST-05 Master Customer (`mt_companies` type customer) — import Excel sungguhan (laporan 22):
 * `/api/v1/wms/master-customer/import-preview` dan `import-commit`. Simpan manual tetap lewat Directus `/items/mt_companies`.
 * Aturan baris (BA 12A MST-05): Kode, Nama, Kota, Alamat wajib ("Alamat kosong" ditolak); kode kembar dilewati.
 */

const PAGE = 'MST-05'
const WRITE_MESSAGE = 'Import customer hanya untuk Admin WH FG.'
export const CUSTOMER_IMPORT_FILE = 'Templat Import Customer.xlsx'
/** Judul kolom templat (sama dengan `CUSTOMER_IMPORT_COLUMNS` di service-library; dicek oleh uji). */
export const CUSTOMER_IMPORT_HEADERS = ['Kode Customer', 'Nama', 'Kota', 'Alamat Lengkap']
const CODE = /^[A-Z0-9][A-Z0-9-]{1,19}$/

type Verdict = ImportCheck & { row?: { code: string; name: string; city: string; address: string } }

function verdictOf(raw: Record<string, string>, seen: Set<string>): Verdict {
  const code = up(raw['Kode Customer'])
  if (!code) return { state: 'invalid', reason: 'Kode Customer wajib diisi' }
  if (!CODE.test(code)) return { state: 'invalid', reason: 'Kode 2–20 huruf/angka/strip, tanpa spasi' }
  const again = seen.has(code)
  seen.add(code)
  if (again) return { state: 'duplicate', reason: 'Kode dobel di berkas' }
  if (readTable('mt_companies').some(row => up(row.code) === code))
    return { state: 'duplicate', reason: 'Kode sudah ada' }
  const name = text(raw.Nama)
  const city = text(raw.Kota)
  const address = text(raw['Alamat Lengkap'])
  if (!name) return { state: 'invalid', reason: 'Nama kosong' }
  if (!city) return { state: 'invalid', reason: 'Kota kosong' }
  if (!address) return { state: 'invalid', reason: 'Alamat kosong' }
  return { state: 'valid', row: { code, name, city, address } }
}

export function customerImportPreview(body: ImportRowsBody) {
  requireWrite(PAGE, WRITE_MESSAGE)
  const seen = new Set<string>()
  const rows: ImportRow[] = importRowsOf(body).map(({ n, raw }) => ({
    n,
    cells: [up(raw['Kode Customer']), raw.Nama, raw.Kota, raw['Alamat Lengkap']],
    ...verdictOf(raw, seen)
  }))
  return importSummary(importFileOf(body, CUSTOMER_IMPORT_FILE), rows)
}

export function customerImportCommit(body: ImportRowsBody) {
  requireWrite(PAGE, WRITE_MESSAGE)
  const seen = new Set<string>()
  let imported = 0
  let skipped = 0
  let rejected = 0
  importRowsOf(body).forEach(({ raw }) => {
    const verdict = verdictOf(raw, seen)
    if (verdict.state === 'duplicate') skipped += 1
    else if (verdict.state === 'invalid' || !verdict.row) rejected += 1
    else {
      insertRow('mt_companies', {
        ...auditFieldsForCreate(),
        id: newId(),
        ...verdict.row,
        type: 'customer',
        phone: null,
        is_active: true
      })
      imported += 1
    }
  })
  if (!imported) fail('Tidak ada baris valid untuk diimport.')
  masterLog(
    'FG',
    PAGE,
    'mst-import',
    importFileOf(body, CUSTOMER_IMPORT_FILE),
    `Import ${imported} customer (dilewati ${skipped} duplikat, ditolak ${rejected})`
  )
  return { imported, skipped, rejected }
}

export function registerMasterCustomerHandlers(mock: MockAdapter) {
  const base = '/api/v1/wms/master-customer'
  mock
    .onPost(`${base}/import-preview`)
    .reply(config => handle(() => customerImportPreview(parse<ImportRowsBody>(config.data))))
  mock
    .onPost(`${base}/import-commit`)
    .reply(config => handle(() => customerImportCommit(parse<ImportRowsBody>(config.data))))
}
